import { useEffect } from 'react';

import { Flex } from '@revenuemarket/cds';
import { thematicColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { useFullSpinnerStore } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { Button, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  ExecuteWiringRequest,
  WIRING_RESULT_STATUS,
  WiringResultItem,
  WiringType,
} from '~apis/wiring';
import useBusinessEntityListQuery from '~hooks/queries/useBusinessEntityListQuery';
import {
  useExecuteWiring,
  useRetryWiring,
  useWiringExecutionCheck,
} from '~hooks/queries/wiring/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import { WiringDepositInfoModel } from './types';

const mappingRequest = (model: WiringDepositInfoModel): ExecuteWiringRequest => {
  const defaultTitle = `${
    model.depositInfo.find((item) => item.senderDescription)?.senderDescription ||
    model.depositInfo.find((item) => item.receiverDescription)?.receiverDescription ||
    model.depositInfo.find((item) => item.amount)?.amount.toLocaleString() + '원' ||
    ''
  } ${model.depositInfo.length > 1 ? `외 ${model.depositInfo.length - 1}건` : '1건'}`;

  const wiringBank = model?.wiringBank ?? 'HANA';
  if (wiringBank === 'HANA') {
    return {
      accountId: model?.accountInfoId,
      isCompressed: Boolean(model.compressInfo?.isCompress),
      compressedMessage: undefined,
      scheduledAt: undefined,
      accountNumber: model.accountNumber,
      title: `${model.title || defaultTitle}`,
      wiringType: model.isPayroll ? 'SALARY' : 'NORMAL',
      wiringBank,
      recordList: model.depositInfo.map((item) => {
        return {
          receiverBankCode: item.organizationCode,
          receiverAccountNumber: item.accountNumber,
          receiverName: item.accountCheckedHolder,
          amount: item.amount,
          senderDescription: item.senderDescription,
          receiverDescription: item.receiverDescription,
          memo: item.memo,
          businessEntityId: item?.businessEntityId,
          taxInvoiceKey: item.taxInvoiceId
            ? {
                taxInvoiceId: item.taxInvoiceId,
                taxInvoiceType: 'PURCHASE',
              }
            : undefined,
        };
      }),
    };
  }

  const getWiringType = () => {
    if (
      (model?.compressInfo?.isCompress && !model.isPayroll) ||
      (!model?.compressInfo?.isCompress && !model.isPayroll && model.depositInfo?.length >= 50)
    ) {
      return 'BULK';
    }

    if (model.isPayroll) {
      return 'SALARY';
    }

    return 'NORMAL';
  };
  return {
    accountId: model?.accountInfoId,
    accountNumber: model.accountNumber,
    isCompressed: Boolean(model.compressInfo?.isCompress),
    compressedMessage: model.compressInfo?.compressMemo
      ? `_${model.compressInfo?.compressMemo}`
      : '',
    title: `${model.title || defaultTitle}`,
    wiringType: getWiringType(),
    scheduledAt: model.reservationInfo.isReservation
      ? dayjs(`${model.reservationInfo.reservationDate} ${model.reservationInfo.reservationTime}`)
          .add(9, 'hour')
          .toISOString()
      : undefined,
    wiringBank,
    recordList: model.depositInfo.map((item) => {
      return {
        receiverBankCode: item.organizationCode,
        receiverName: item.accountCheckedHolder,
        receiverAccountNumber: item.accountNumber,
        amount: item.amount,
        senderDescription: item.senderDescription,
        receiverDescription: item.receiverDescription,
        memo: item.memo,
        businessEntityId: item?.businessEntityId,
        taxInvoiceKey: item.taxInvoiceId
          ? {
              taxInvoiceId: item.taxInvoiceId,
              taxInvoiceType: 'PURCHASE',
            }
          : undefined,
      };
    }),
  };
};

interface Props {
  model: WiringDepositInfoModel;
  onBack: () => void;
  onFinish: (result: {
    items: WiringResultItem[];
    status: WIRING_RESULT_STATUS;
    wiringType: WiringType;
    code?: string;
    message?: string;
  }) => void;
  wiringId: number | null;
  setWiringId: (id: number) => void;
}

const WiringBankConnectStep: React.FC<Props> = ({
  model,
  onBack,
  onFinish,
  wiringId,
  setWiringId,
}) => {
  const { companyId } = useCompanyId();
  const wiringBank = model?.wiringBank ?? 'HANA';
  const { showFullSpinner, hideFullSpinner } = useFullSpinnerStore();
  const { mutateAsync: executeWiring } = useExecuteWiring();
  const { mutateAsync: wiringCheck } = useWiringExecutionCheck();
  const { mutateAsync: retryWiring } = useRetryWiring();
  const { data: businessEntityData } = useBusinessEntityListQuery({
    companyId: companyId as string,
    favoriteOnly: false,
  });
  const onWiringExecutionCheck = async () => {
    if (!wiringId) return;
    const response = await wiringCheck({ companyId, wiringId: wiringId! });
    if (response.Status === ResponseStatus.SUCCESS) {
      if (
        response?.Data?.data.status === 'SUCCESS' ||
        response?.Data?.data.status === 'PARTIAL_FAILED' ||
        response?.Data?.data.status === 'FAILED' ||
        response?.Data?.data.status === 'PROCESSING'
      ) {
        mixpanel.track(MIXPANEL_EVENT.WIRING.EXECUTE_WIRE_TRANSFER);
        onFinish({
          wiringType: response?.Data?.data.wiringType,
          status: response?.Data?.data.status,
          items: response?.Data?.data.recordList.map((item) => ({
            ...item,
            businessEntityLabel: businessEntityData?.Data?.data?.find(
              (d) => d.id === item.businessEntityId,
            )?.name,
          })),
          code: response?.Data?.data.resultCode,
          message: response?.Data?.data.resultMessage,
        });
      }
    } else {
      Toast.error(response.ErrorMsg || '이체 실행에 실패했습니다');
    }
  };

  const submitFormToKB = (url: string, ott: string, mntaccInfo: string) => {
    const popup = window.open('', 'popupWindow', 'width=1200,height=1000'); // Open popup window

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url;
    form.target = 'popupWindow'; // Set the target to the popup window

    // ott input field
    const ottInput = document.createElement('input');
    ottInput.type = 'hidden';
    ottInput.name = 'ott';
    ottInput.value = ott;
    form.appendChild(ottInput);

    // mntaccInfo input field
    const mntaccInfoInput = document.createElement('input');
    mntaccInfoInput.type = 'hidden';
    mntaccInfoInput.name = 'mntaccInfo';
    mntaccInfoInput.value = mntaccInfo;
    form.appendChild(mntaccInfoInput);

    document.body.appendChild(form);

    form.submit(); // Submit form to popup
    document.body.removeChild(form); // Clean up form after submission
  };

  const execute = async (request: ExecuteWiringRequest) => {
    showFullSpinner('이체 화면 연동중입니다');
    const result = await executeWiring({
      companyId,
      request,
    });
    const data = result?.Data?.data;

    if (result.Status === ResponseStatus.SUCCESS && data) {
      hideFullSpinner();
      setWiringId(data.wiringId);
      if (request.wiringBank === 'HANA') {
        window.open(data.url, '_blank', 'width=1200, height=1000');
      } else {
        submitFormToKB(
          data.url || 'https://zobiz.kbstar.com/quics?page=C108004',
          data.data?.ott ?? '',
          data.data?.mntaccInfo ?? '',
        );
      }
    } else {
      Toast.error(result.ErrorMsg || '이체 실행에 실패했습니다');
      hideFullSpinner();
    }
  };

  useEffect(() => {
    if (model && companyId) {
      const request = mappingRequest(model);

      execute(request);
    }
  }, [model, companyId]);

  const onRetry = async () => {
    if (!wiringId) return;

    showFullSpinner('이체 화면 연동중입니다');
    const result = await retryWiring({
      companyId,
      wiringId,
    });

    if (result.Status === ResponseStatus.SUCCESS) {
      hideFullSpinner();
      if (wiringBank === 'HANA') {
        window.open(result?.Data?.data.url, '_blank', 'width=1200, height=800');
      } else {
        const request = mappingRequest(model);
        execute(request);
      }
    }
  };

  const selectBank = wiringBank === 'HANA' ? '하나은행' : 'KB국민은행';

  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap={24}
      style={{
        height: '30vh',
      }}
    >
      <Typography
        style={{
          fontSize: 14,
        }}
      >
        {selectBank} 화면에서 이체 실행을 완료한 뒤 확인 버튼을 클릭해주세요.
      </Typography>
      <Button type="primary" onClick={onWiringExecutionCheck}>
        확인
      </Button>
      <Typography.Text
        style={{
          color: thematicColors.primary.medium,
          cursor: 'pointer',
        }}
        onClick={onRetry}
      >
        은행 화면이 보이지 않으면 여기를 클릭해주세요.
      </Typography.Text>
    </Flex>
  );
};

export default WiringBankConnectStep;
